import { apiService } from '@/service/api'
import type { DocumentRecord } from '@/types/catalog'

// The legacy `POST /v1/document/query` endpoint hard-codes `page_size: 20`
// server-side (backend/web `legacy-documents.controller.ts`). A single call
// with more than 20 `doc_ids` silently truncates the response to 20 records —
// with no signal to the caller — so any consumer resolving a larger id set
// must chunk. Originally discovered in FS-483 (board file names) and reused by
// FS-511 (board pnid hydration); keeping the chunking in one place means the
// chunk size only has to move when the backend cap moves.
export const QUERY_DOCS_CHUNK_SIZE = 20

/**
 * Resolve a list of document ids via the batch `/v1/document/query` endpoint,
 * chunking into `chunkSize` (default {@link QUERY_DOCS_CHUNK_SIZE}) groups and
 * firing the chunks in parallel so the caller pays one round-trip's latency
 * rather than one per chunk. Returns the flattened records.
 *
 * Missing ids (stale references) are simply absent from the result — the batch
 * endpoint omits them rather than erroring the whole chunk, so callers should
 * treat a smaller-than-requested result as "some ids did not resolve".
 *
 * @param docIds  ids to resolve (deduplicate upstream if needed)
 * @param options.signal    optional AbortSignal, forwarded to every chunk
 * @param options.chunkSize override the per-request chunk size
 */
export async function batchQueryDocs(
  docIds: string[],
  options?: { signal?: AbortSignal; chunkSize?: number; includePnidSource?: boolean }
): Promise<DocumentRecord[]> {
  if (docIds.length === 0) return []

  const chunkSize = options?.chunkSize ?? QUERY_DOCS_CHUNK_SIZE
  const chunkRequests: Promise<DocumentRecord[]>[] = []
  for (let i = 0; i < docIds.length; i += chunkSize) {
    const chunk = docIds.slice(i, i + chunkSize)
    chunkRequests.push(
      apiService.queryDocs(
        {
          type: undefined,
          doc_ids: chunk,
          // Request a page as large as the chunk so the server (which now honors
          // page_size, clamped) returns the whole chunk instead of re-truncating
          // a >50-id chunk back down to its default.
          page_size: chunkSize,
          // FS-511 (opt-in): ask the server to resolve graph->pnid sources inline.
          ...(options?.includePnidSource ? { include_pnid_source: true } : {}),
        },
        { signal: options?.signal }
      )
    )
  }

  const chunks = await Promise.all(chunkRequests)
  return chunks.flat()
}
